import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom"
import Filter from "pages/purchase/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination, Transition } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { useResultSet } from "repositories/db-repository"
import {
  getBranchList,
  buildOutletFilterOptions,
  getCurrentEntity,
  getDefaultOutletFilterValueFrom,
  resolveOutletFilterWith,
} from "helpers/general-helper"
import useBranchRepositories from "repositories/branch"
import { getUser } from "helpers/auth-helper"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import StatusInfo from "components/status-info" // 150726 (ATALLA-322)

// 150726 (ATALLA-322): penjelasan singkat tiap status retur pembelian
// utk tombol tanda tanya di samping filter status.
const RETURN_STATUS_INFO = [
  { value: "DRAFT", label: "DRAFT", description: "Belum diposting; stok belum keluar." },
  { value: "SUBMITTED", label: "SUBMITTED", description: "Diajukan, menunggu persetujuan." },
  { value: "APPROVED", label: "APPROVED", description: "Disetujui, siap diposting." },
  { value: "REJECTED", label: "REJECTED", description: "Ditolak sebelum diposting." },
  { value: "POSTED", label: "POSTED", description: "Stok keluar; utk supplier internal stok di-mirror ke supplier." },
  { value: "CANCELLED", label: "CANCELLED", description: "Dibatalkan." },
]

export default function PurchaseReturnList() {
  const { can, isSuper, currentBranch, canFilterAllOutlets } = useAuth()
  const { title, transition, setTransition } = useOutletContext()
  const location = useLocation()

  const [searchParams, setSearchParams] = useSearchParams()

  // Kalau user terkunci ke 1 outlet (bukan super), filter outlet dipaksa ke outlet itu.
  const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya

  const page = searchParams.get("page")
  const status = searchParams.get("status")
  const receiptId = searchParams.get("receipt_id")
  const poId = searchParams.get("po_id")
  const dateFrom = searchParams.get("date_from") ?? ""
  const dateTo = searchParams.get("date_to") ?? ""
  const searchParam = searchParams.get("search") ?? ""

  // Local state untuk search (submit on Enter)
  const [search, setSearch] = useState(searchParam)
  const searchInput = useRef(null)

  useEffect(() => setSearch(searchParam), [searchParam])

  const branchSelection = getBranchList()

  // 270726 (permintaan user): opsi filter outlet LINTAS-ENTITAS (pola list pembelian) —
  // user Vision harus bisa memilih outlet HO Atalla (id 1) & grup HO untuk melihat retur
  // dari HO Atalla yang barangnya dikembalikan ke Vision. Grup "Semua Outlet" tetap
  // adaptif entitas user. Backend men-scope per KETERLIBATAN entitas (outlet ATAU
  // supplier internal entitas user).
  const { data: allOutletData } = useBranchRepositories({ paginate: false, unrestricted: true })
  const allOutlets = useMemo(
    () => (Array.isArray(allOutletData) ? allOutletData : allOutletData?.data ?? []),
    [allOutletData]
  )

  const outletOptions = useMemo(
    () => buildOutletFilterOptions(allOutlets, getCurrentEntity()),
    [allOutlets]
  )

  // 240726 (permintaan user): default filter "ikut outlet user" (grup HO/Semua Outlet).
  const rawOutletId = searchParams.get("outlet_id")
  const outletFilter = isBranchLocked
    ? String(currentBranch.id)
    : (rawOutletId ?? getDefaultOutletFilterValueFrom(outletOptions, currentBranch?.id))
  // Grup (Semua Outlet/HO) → branch_list; outlet individual → outlet_id tunggal.
  const { outlet_id: resolvedOutletId, branch_list: resolvedBranchList } =
    resolveOutletFilterWith(outletFilter, outletOptions)

  // Map outlet_id -> nama outlet untuk ditampilkan di tabel. Gabungan daftar outlet
  // lengkap + branch_list per-user (fallback bila fetch unrestricted belum siap).
  const branchNameById = useMemo(() => {
    const map = {}
    ;[...(allOutlets || []), ...(branchSelection || [])].forEach((x) => {
      if (x?.id != null && x?.name != null) map[String(x.id)] = x.name
    })
    return map
  }, [allOutlets, branchSelection])

  /**
   * Endpoint list return:
   * - table: purchase_returns
   * - filter: outlet_id, status, receipt_id, po_id, search (return_number / supplier snapshot / etc)
   */
  const { data: result_set, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `purchase-returns`,
    {
      // Grup → branch_list (kumpulan id); outlet individual → outlet_id tunggal.
      outlet_id: resolvedOutletId || undefined,
      branch_list: JSON.stringify(resolvedBranchList),
      page,
      record_per_page: 10,
      search: searchParam || undefined,
      status: status || undefined,
      receipt_id: receiptId || undefined,
      po_id: poId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }
  )

  // kalau kamu punya list_flow reuse component Filter yang sama
  // const { data: list_flow } = useResultSet(
  //   `${process.env.REACT_APP_INVENTORY_API_URL}`,
  //   `list_flow`,
  //   {}
  // )

  useEffect(() => {
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (searchInput.current) searchInput.current.focus()
  }, [])

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  const patchSearchParams = (patch, opts = {}) => {
    const next = new URLSearchParams(searchParams)

    Object.entries(patch || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })

    // reset page ketika filter berubah
    if (
      patch &&
      (Object.prototype.hasOwnProperty.call(patch, "search") ||
        Object.prototype.hasOwnProperty.call(patch, "status") ||
        Object.prototype.hasOwnProperty.call(patch, "outlet_id") ||
        Object.prototype.hasOwnProperty.call(patch, "receipt_id") ||
        Object.prototype.hasOwnProperty.call(patch, "po_id") ||
        Object.prototype.hasOwnProperty.call(patch, "date_from") ||
        Object.prototype.hasOwnProperty.call(patch, "date_to"))
    ) {
      next.delete("page")
    }

    setSearchParams(next, opts)
  }

  const updateFilter = (value) => patchSearchParams(value)
  const removeFilter = () => setSearchParams({})

  const onSearchChange = (val) => setSearch(val)

  const onSearchSubmit = () => patchSearchParams({ search: search, page: 1 })

  const generatePaginationParam = () => {
    let tmp = ""
    if (status) tmp += `&status=${status}`
    if (outletFilter) tmp += `&outlet_id=${outletFilter}`
    if (receiptId) tmp += `&receipt_id=${receiptId}`
    if (poId) tmp += `&po_id=${poId}`
    if (dateFrom) tmp += `&date_from=${dateFrom}`
    if (dateTo) tmp += `&date_to=${dateTo}`
    if (searchParams.has("search")) tmp += `&search=${searchParams.get("search")}`
    return tmp
  }

  const list = useMemo(() => {
    if (Array.isArray(result_set?.data?.data)) return result_set.data?.data
    return []
  }, [result_set])

  const paginationLinks = result_set?.data?.links
  const total = result_set?.data?.total
  const from = result_set?.data?.from
  const to = result_set?.data?.to

  const statusBadge = (st) => {
    const s = String(st || "").toUpperCase()
    const base = "inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium"
    if (s === "POSTED") return <span className={`${base} bg-green-50 text-green-700`}>{st}</span>
    if (s === "DRAFT") return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st}</span>
    if (s === "CANCELLED") return <span className={`${base} bg-red-50 text-red-700`}>{st}</span>
    return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st || "-"}</span>
  }

  const formatDateTime = (value) => {
    if (!value) return "-"

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date)
  }

  const postJson = async (path, body = null) => {
    const url = `${process.env.REACT_APP_INVENTORY_API_URL.replace(/\/$/, "")}${path}`
    // Sisipkan user_id dari sesi login UI (sumber data yang sama dengan useAuth())
    // ke body request, supaya backend bisa mengisi created_by/updated_by.
    // Tanpa ini actor di backend jatuh ke "SYSTEM".
    const userId = getUser()?.id
    const payload = userId ? { user_id: userId, ...(body || {}) } : body
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload ? JSON.stringify(payload) : null,
    })
    const json = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      const msg = json?.message || "Permintaan gagal"
      const err = json?.error ? `: ${json.error}` : ""
      throw new Error(msg + err)
    }
    return json
  }

  const [actingId, setActingId] = useState(null)

  const onPost = async (ret) => {
    if (!ret?.id) return
    if (!window.confirm(`Post Return ${ret.return_number || ""}?`)) return

    try {
      setActingId(ret.id)
      await postJson(`/purchase-returns/${ret.id}/post`)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal post Return")
    } finally {
      setActingId(null)
    }
  }

  const onCancel = async (ret) => {
    if (!ret?.id) return
    const reason = window.prompt(`Cancel Return ${ret.return_number || ""}\nAlasan (opsional):`, "")
    if (reason === null) return

    try {
      setActingId(ret.id)
      await postJson(`/purchase-returns/${ret.id}/cancel`, reason ? { reason } : null)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal cancel Return")
    } finally {
      setActingId(null)
    }
  }

  const canPost = (ret) => String(ret?.status || "").toUpperCase() === "DRAFT"
  const canCancel = (ret) => String(ret?.status || "").toUpperCase() === "DRAFT"

  return (
    <Transition type="fade">
      <div className="pb-4 space-y-6 lg:pb-8">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center sm:space-x-2">
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <circle cx={10} cy={10} r={7}></circle>
                  <line x1={21} y1={21} x2={15} y2={15}></line>
                </svg>
              </div>

              <input
                ref={searchInput}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSearchSubmit() } }}
                value={search}
                type="text"
                placeholder={`${lang.search} Purchase Return`}
                autoComplete="off"
                className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
            </div>

            <OutletFilterSelect
              value={outletFilter}
              onChange={(v) => patchSearchParams({ outlet_id: v })}
              disabled={isBranchLocked}
              options={outletOptions}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
            />

            <select
              value={status || ""}
              onChange={(e) => patchSearchParams({ status: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            >
              <option value="">Semua Status</option>
              <option value="DRAFT">DRAFT</option>
              <option value="SUBMITTED">SUBMITTED</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="POSTED">POSTED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>

            {/* 150726 (ATALLA-322): tombol tanda tanya penjelasan status retur. */}
            <StatusInfo
              className="hidden sm:inline-flex self-center"
              statuses={RETURN_STATUS_INFO}
            />

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => patchSearchParams({ date_from: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              title="Tanggal Dari"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => patchSearchParams({ date_to: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              title="Tanggal Sampai"
            />
          </div>

          <div className="flex items-center space-x-2">
            {can("return-order.create") && (
              <div>
                <Link
                  to="create"
                  onClick={() => setTransition({ type: "slide", reverse: true })}
                  state={{ back: title, from: location?.pathname, transition: { type: "slide" } }}
                  className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
                >
                  <Plus className="w-4 h-4" stroke={1.5} />
                  <span>{lang.create}</span>
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
            <thead className="bg-neutral-50 rounded-t-3xl">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">No. Return</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Outlet</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">PO</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">GR</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Supplier</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Tanggal</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Item</th>
                {/* 280726 (permintaan user): total nilai HPP yang diretur. */}
                <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500 whitespace-nowrap">Nilai HPP</th>
                <th className="relative px-3 py-3"><span className="sr-only">{lang.action}</span></th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-neutral-200">
              {isLoading && (
                <tr className="text-center">
                  <td colSpan={9} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.loading_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && !searchParam && (
                <tr className="text-center">
                  <td colSpan={9} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && searchParam && (
                <tr className="text-center">
                  <td colSpan={9} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_result}
                  </td>
                </tr>
              )}

              {!isLoading && list.map((ret) => (
                <tr key={ret.id} className="hover:bg-brand-50 transition-colors">
                  <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                    {ret.return_number ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {branchNameById[String(ret.outlet_id)] ?? ret.outlet_id ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {ret?.purchase_order?.po_number ?? ret.po_id ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {ret.goods_receipt?.gr_number ?? ret.receipt_id ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {ret?.purchase_order?.supplier_name_snapshot ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {formatDateTime(ret.updated_at) ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs whitespace-nowrap">
                    {statusBadge(ret.status)}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {ret.items_count ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs font-semibold text-right text-neutral-900 whitespace-nowrap">
                    {Number(ret.total_hpp ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })}
                  </td>

                  <td className="px-3 py-4 text-xs font-medium text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      {(can("return-order.update") || can("return-order.view")) && (
                        <Link
                          to={`${ret.id}/edit`}
                          state={{ back: title, from: location?.pathname, transition: "slide" }}
                          className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90"
                        >
                          <Pencil className="w-6 h-6" />
                        </Link>
                      )}

                      {can("return-order.post") && canPost(ret) && (
                        <button
                          type="button"
                          disabled={actingId === ret.id}
                          onClick={() => onPost(ret)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-green-700 rounded-lg hover:bg-green-600 disabled:opacity-60"
                        >
                          {actingId === ret.id ? "..." : "Posting"}
                        </button>
                      )}

                      {can("return-order.cancel") && canCancel(ret) && (
                        <button
                          type="button"
                          disabled={actingId === ret.id}
                          onClick={() => onCancel(ret)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-500 disabled:opacity-60"
                        >
                          {actingId === ret.id ? "..." : "Batal"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          params={generatePaginationParam()}
          links={paginationLinks}
          from={from}
          to={to}
          total={total}
        />
      </div>
    </Transition>
  )
}